import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Paper,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  hasDecidedCookiePreferences,
  saveCookiePreferences,
} from '../utils/analytics';

const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!hasDecidedCookiePreferences()) {
      setOpen(true);
    }
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    saveCookiePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
    setOpen(false);
  };

  const rejectOptional = () => {
    saveCookiePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
    setOpen(false);
  };

  return (
    <Fade in={open} timeout={280}>
      <Box
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-desc"
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1400,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          px: { xs: 1.5, sm: 3 },
          pb: {
            xs: 'max(12px, env(safe-area-inset-bottom))',
            sm: 'max(20px, env(safe-area-inset-bottom))',
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            pointerEvents: 'auto',
            width: '100%',
            maxWidth: 640,
            borderRadius: 2.5,
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, 0.96),
            border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
            boxShadow: `0 -8px 40px ${alpha('#000', 0.45)}`,
            backdropFilter: 'blur(12px)',
          }}
        >
          <Box
            sx={{
              height: 3,
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(
                theme.palette.primary.light,
                0.35,
              )} 100%)`,
            }}
          />
          <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box>
              <Typography
                id="cookie-consent-title"
                variant="subtitle1"
                sx={{ fontWeight: 700, letterSpacing: 0.01, mb: 0.75 }}
              >
                {t('cookies.bannerTitle', 'Cookies')}
              </Typography>
              <Typography
                id="cookie-consent-desc"
                variant="body2"
                sx={{ color: 'text.secondary', lineHeight: 1.55 }}
              >
                {t(
                  'cookies.bannerBody',
                  'We use analytics cookies to understand bookings and improve Band Space. Necessary cookies always stay on.',
                )}{' '}
                <Box
                  component={RouterLink}
                  to="/cookies"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('cookies.managePreferences', 'Manage preferences')}
                </Box>
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1}
              justifyContent="flex-end"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button
                variant="text"
                color="inherit"
                onClick={rejectOptional}
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.06) },
                }}
              >
                {t('cookies.rejectOptional', 'Necessary only')}
              </Button>
              <Button
                variant="contained"
                onClick={acceptAll}
                sx={{
                  fontWeight: 700,
                  px: 2.5,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                {t('cookies.acceptAll', 'Accept all')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Fade>
  );
};

export default CookieConsentBanner;
